import axios from 'axios';
import { useQuery, useMutation } from 'react-query';
import { useMemo } from 'react';



export function useFetchProjectDetails(token) {
  // Memoize the query key for consistency across renders
  const queryKey = useMemo(() => ["projectDetails", token], [token]);

  // Memoize the fetch function for stable dependency reference
  const fetchFunction = useMemo(
    () => async () => {
      if (!token) {
        throw new Error("Token is missing");
      }
      const response = await axios.get("/api/project-details", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    [token]
  );

  // Use `useQuery` for data fetching with optimized options
  return useQuery(queryKey, fetchFunction, {
    enabled: !!token, // Prevents fetching if token is not available
    staleTime: 1000 * 60 * 5, // Data remains fresh for 5 minutes
    cacheTime: 1000 * 60 * 10, // Cache data for 10 minutes
    refetchOnWindowFocus: false, // Avoids refetching on window focus
    retry: 2, // Retry failed requests up to 2 times
    refetchOnMount: false, // Skip fetching when the component mounts if data is cached
    keepPreviousData: true, // Keeps showing old data until new data is fetched
  });
}

// ['projectDetailsOptimized', projectIds, startDate, endDate, page, limit]
export function useFetchProjectDetailsOptimized(token, selectedProjects, startDate, endDate, selectedStatus, page, limit) {
  return useQuery(
    ['projectDetailsOptimized', token, { selectedProjects, startDate, endDate, selectedStatus, page, limit }],
    async () => {
      const response = await axios.post(`/api/project-details-optimized`,
        { page, limit, selectedProjects, selectedStatus, startDate, endDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !!token,
    }
  );
}

export function useFetchProjectPrintOptimized(token, selectedProjects, startDate, endDate) {
  return useQuery('projectPrintOptimized', async () => {
    const response = await axios.post(`/api/project/export/optimized`,
      {
        selectedProjects,
        startDate,
        endDate,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    return response.data;
  });
}


export function useFetchProjectManager(token) {
  const queryKey = useMemo(() => 'projectManagers', []);

  const fetchFunction = useMemo(() => async () => {
    const response = await axios.get('/api/user/user-project-manager', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }, [token]);

  return useQuery(queryKey, fetchFunction, {
    enabled: !!token,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useFetchClientProjectManager(token, clientId) {
  const queryKey = useMemo(() => ['clientProjectManagers', clientId], [clientId]);

  const fetchFunction = useMemo(() => async () => {
    const response = await axios.get(`/api/client/get-project-managers/${clientId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.project_managers;
  }, [token, clientId]);

  return useQuery(queryKey, fetchFunction, {
    enabled: !!token && !!clientId,
    staleTime: 3 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useFetchClientDetails(token) {
  const queryKey = useMemo(() => 'clientDetails', []);

  const fetchFunction = useMemo(() => async () => {
    const response = await axios.get('/api/client-details', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }, [token]);

  return useQuery(queryKey, fetchFunction, {
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useFetchSprintProjectDetails(token, projectId) {
  const queryKey = useMemo(() => ['sprintProjectDetails', projectId], [projectId]);

  const fetchFunction = useMemo(() => async () => {
    const response = await axios.get(`/api/sprintProject-details/${projectId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }, [token, projectId]);

  return useQuery(queryKey, fetchFunction, {
    enabled: !!token && !!projectId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useDeleteProjectDetails(token, selectedId) {
  const mutation = useMutation(async () => {
    const response = await axios.get(`/api/project-details/delete/${selectedId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  });

  return mutation;
}

export function useUpdateProjectDetails(token, selectedId, requestData) {
  const mutation = useMutation(async () => {
    const response = await axios.put(`/api/project-details/update/${selectedId}`, requestData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  });

  return mutation;
}

export function useDeleteSprintDetails(token, selectedSprintId) {
  const mutation = useMutation(async () => {
    const response = await axios.get(`/api/sprint-details/delete/${selectedSprintId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  });

  return mutation;
}

export function useUpdateSprintDetails(token, selectedSprintId, requestData) {
  const mutation = useMutation(async () => {
    const response = await axios.put(`/api/sprint-details/update/${selectedSprintId}`, requestData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  });

  return mutation;
}